import MultipleCheckboxesModalInput from '@/common/components/base/input/multipleCheckboxesModalInput/MultipleCheckboxesModalInput';
import MainModal from '@/common/components/base/modal/Modal';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import { getImageTitlesQueryOptions } from '@/common/queries/selections/ImageTitles/getImageTitlesQueryOptions';
import { getVehiclePartsQueryOptions } from '@/common/queries/selections/vehicleParts/getVehiclePartsQueryOptions';
import {
  damaged_entity_type,
  FileTypeConst,
  owner_type,
} from '@/common/types/Files.type';
import { CorrectiveImageModalType } from '@/modules/adjusterReportModules/IncidentParticipants/components/images/property/damaged/correctiveImages/components/inquiredForm/components/modals/CorrectiveImageModal.type';
import { getVehicleInformationOption } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getVehicleInformation/getVehicleInformationOption';
import { participantsIncidentQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/participantsIncidentList/participantsIncidentQueryOptions';
import { useCorrectiveImages } from '@/modules/adjusterReportModules/IncidentParticipants/store/correctiveImages/useCorrectiveImages';
import { DamagedVehicleCorrectiveImageNameType } from '@/modules/adjusterReportModules/IncidentParticipants/types/images';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Box } from 'styled-system/jsx';

const CorrectiveImageModal: React.FC<CorrectiveImageModalType> = (props) => {
  const { damagedId, id } = useParams();
  const [damagedType, setDamagedType] = useState<{
    type: damaged_entity_type;
    owner: owner_type;
  } | null>(null);
  const methods = useForm<{
    damaged_parts: { label: string; value: string }[];
    documents: { label: string; value: string }[];
    vehicle: { label: string; value: string }[];
  }>();
  const {
    setSelectedDamagedParts,
    setSelectedVehicle,
    setSelectedDocuments,
    selectedDamagedParts,
    selectedVehicle,
    selectedDocuments,
    preFetchedCorrectiveImages,
  } = useCorrectiveImages();

  const { data: vehicleInformationData } = useQuery({
    ...getVehicleInformationOption({
      type: props?.requestType as unknown as FileTypeConst,
      damaged_id: +damagedId!,
      id: +id!,
    }),
    enabled: damagedType?.type ? damagedType?.type !== 'OTHER' : false,
  });

  const { data: damageList } = useQuery({
    ...participantsIncidentQueryOptions({
      id: id!,
    }),
    enabled:
      props.type === DamagedVehicleCorrectiveImageNameType.documents &&
      props.requestType !== 'initial',
  });

  const { data: ImageDocumentSelectionTitles } = useQuery({
    ...getImageTitlesQueryOptions({
      file_type: (props?.requestType as FileTypeConst) || 'property',
      image_type: 'document',
      user_type:
        props?.requestType === 'body' || props?.requestType === 'initial'
          ? 'insurer'
          : 'damaged',
      damaged_entity_type: damagedType?.type! ?? null,
      owner_type: damagedType?.owner! ?? null,
    }),
    enabled:
      props.type === DamagedVehicleCorrectiveImageNameType.documents &&
      damagedType !== null,
  });

  const { data: ImageVehicleSelectionTitles } = useQuery({
    ...getImageTitlesQueryOptions({
      file_type: (props?.requestType as FileTypeConst) || 'property',
      image_type: 'vehicle',
      user_type:
        props?.requestType === 'body' || props?.requestType === 'initial'
          ? 'insurer'
          : 'damaged',
      damaged_entity_type: damagedType?.type! ?? null,
      owner_type: damagedType?.owner! ?? null,
      vehicle_type:
        vehicleInformationData?.vehicle_model?.type === 2
          ? 'motorcycle'
          : 'car',
    }),
    enabled: props.type === DamagedVehicleCorrectiveImageNameType.vehicle,
  });

  const { data: ImageDamagedPartsTitles } = useQuery({
    ...getVehiclePartsQueryOptions({
      id: null,
      side: null,
      vehicle_type: vehicleInformationData?.vehicle_model?.type,
    }),
    enabled: props.type === DamagedVehicleCorrectiveImageNameType.damaged_parts,
  });

  const TitleDescriptionMapper = {
    documents: 'مدارک فردی',
    vehicle: 'تصاویر وسیله‌نقلیه',
    damaged_parts: 'نقاط اسیب دیده',
  };

  useEffect(() => {
    methods.reset({
      damaged_parts: selectedDamagedParts?.map((item) => ({
        label: item.name,
        value: item.name,
      })),
      documents: selectedDocuments?.map((item) => ({
        label: item.name,
        value: item.name,
      })),
      vehicle: selectedVehicle?.map((item) => ({
        label: item.name,
        value: item.name,
      })),
    });
  }, [
    selectedDamagedParts,
    selectedVehicle,
    selectedDocuments,
    preFetchedCorrectiveImages,
    methods,
  ]);

  useEffect(() => {
    const currentDamaged = damageList?.damages.find(
      (item) => item.id === +damagedId!,
    );
    setDamagedType({
      type: currentDamaged?.damaged_type.const! as damaged_entity_type,
      owner: currentDamaged?.owner_type.const!,
    });
  }, [damageList]);

  return (
    <MainModal open={props.open} closeModal={() => props.closeModal()}>
      <TitleDescription>{TitleDescriptionMapper[props.type]}</TitleDescription>
      <FormProvider {...methods}>
        <Box
          h={300}
          overflowY="auto"
          mt={24}
          display={'flex'}
          flexDirection={'column'}
        >
          {props.type ===
            DamagedVehicleCorrectiveImageNameType.damaged_parts && (
            <MultipleCheckboxesModalInput
              name="damaged_parts"
              key={'damaged_parts'}
              data={ImageDamagedPartsTitles ?? []}
              hasInput="omit"
              CheckedTheDisabledCheckBoxes={true}
              disabledCheckBoxes={preFetchedCorrectiveImages.map((item) => ({
                label: item.title,
                value: item.title,
              }))}
              outerOpen={
                props.type ===
                DamagedVehicleCorrectiveImageNameType.damaged_parts
              }
              onChange={(data) => {
                const selectedDamgeds = data.map((item) => ({
                  name: item.label,
                  type: TitleDescriptionMapper[
                    DamagedVehicleCorrectiveImageNameType.damaged_parts
                  ],
                  enNameOrId: ImageDamagedPartsTitles?.find(
                    (singleItem) => singleItem.label === item.label,
                  )?.enNameOrId?.toString()!,
                }));
                setSelectedDamagedParts(selectedDamgeds);
                props.closeModal();
              }}
              defaultValue={selectedDamagedParts.map((item) => ({
                label: item.name,
                value: item.name,
              }))}
            />
          )}
          {props.type === DamagedVehicleCorrectiveImageNameType.documents && (
            <MultipleCheckboxesModalInput
              key={'documents'}
              name="documents"
              data={ImageDocumentSelectionTitles ?? []}
              hasInput="omit"
              disabledCheckBoxes={preFetchedCorrectiveImages.map((item) => ({
                label: item.title,
                value: item.title,
              }))}
              CheckedTheDisabledCheckBoxes={true}
              outerOpen={
                props.type === DamagedVehicleCorrectiveImageNameType.documents
              }
              onChange={(data) => {
                const selectedDocs = data.map((item) => ({
                  name: item.label,
                  type: TitleDescriptionMapper[
                    DamagedVehicleCorrectiveImageNameType.documents
                  ],
                  enNameOrId: ImageDocumentSelectionTitles?.find(
                    (singleItem) => singleItem.label === item.label,
                  )?.enNameOrId!,
                }));
                setSelectedDocuments(selectedDocs);
                props.closeModal();
              }}
              defaultValue={selectedDocuments.map((item) => ({
                label: item.name,
                value: item.name,
              }))}
            />
          )}
          {props.type === DamagedVehicleCorrectiveImageNameType.vehicle && (
            <MultipleCheckboxesModalInput
              name="vehicle"
              key={'vehicle'}
              data={ImageVehicleSelectionTitles ?? []}
              hasInput="omit"
              CheckedTheDisabledCheckBoxes={true}
              disabledCheckBoxes={preFetchedCorrectiveImages.map((item) => ({
                label: item.title,
                value: item.title,
              }))}
              outerOpen={
                props.type === DamagedVehicleCorrectiveImageNameType.vehicle
              }
              defaultValue={selectedVehicle.map((item) => ({
                label: item.name,
                value: item.name,
              }))}
              onChange={(data) => {
                const selectedVehicle = data.map((item) => ({
                  name: item.label,
                  type: TitleDescriptionMapper[
                    DamagedVehicleCorrectiveImageNameType.vehicle
                  ],
                  enNameOrId: ImageVehicleSelectionTitles?.find(
                    (singleItem) => singleItem.label === item.label,
                  )?.enNameOrId!,
                }));
                setSelectedVehicle(selectedVehicle);
                props.closeModal();
              }}
            />
          )}
        </Box>
      </FormProvider>
    </MainModal>
  );
};
export default CorrectiveImageModal;
